import React, {
  useCallback,
  useEffect,
  useState,
  useMemo,
  useContext,
  createContext,
} from 'react';
import { useRouter } from 'next/router';

import { Button } from '@tmap/mmm-style-guide/src/Button';
import { Grid } from '@tmap/mmm-style-guide/src/Grid';
import { Divider } from '@tmap/mmm-style-guide/src/Divider';
import { Typography } from '@tmap/mmm-style-guide/src/Typography';
import { styled } from '@tmap/mmm-style-guide/src/styled';
import SelectField from '@tmap/mmm-style-guide/src/fields/SelectField';
import { Box } from '@tmap/mmm-style-guide/src/Box';
import { FilterList, SortIcon } from '@tmap/mmm-style-guide/src/Icon';
import { Badge } from '@tmap/mmm-style-guide/src/Badge';
import { Radio, RadioGroup } from '@tmap/mmm-style-guide/src/Radio';
import { FormControlLabel } from '@tmap/mmm-style-guide/src/Form';
import { MenuItem } from '@tmap/mmm-style-guide/src/MenuItem';
import useDebounced from '@tmap/mmm-style-guide/src/useDebounced';

import ResponsiveBottomDrawer from '../responsiveBottomDrawer';
import RoundedDrawer from './roundedDrawer';
import FiltersSelector from './filtersSelector';
import addObjectParamsToPath from '../../lib/addObjectParamsToPath';
import useCurrentUser from '../../hooks/useCurrentUser';
import useQuestionModule from '../../hooks/useQuestionModule';

// [state, setState]
export const ResetFiltersContext = createContext([false, () => { }]);

const OutlinedBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge:not(.MuiBadge-invisible)': {
    border: `1px solid ${theme.palette.primary.main}`,
    background: theme.palette.common.white,
    color: theme.palette.primary.main,
    transform: 'scale(1) translate(25%, -25%)',
  },
}));

const SelectFieldStyles = styled(Box)(({ theme }) => ({
  display: 'contents',
  '& .MuiFormControl-root': {
    background: theme.palette.grey[50],
  },
}));

const FiltersButton = styled(Button)(({ theme }) => ({
  '& button': {
    padding: theme.spacing(1),
    minWidth: '40px',
    backgroundColor: theme.palette.primary.dark,
  },
}));

function didQueryChange(newQuery, oldQuery) {
  return Object.entries(newQuery)
    .some(([k, v]) => k !== 'page' && (oldQuery?.[k]?.toString() || '') !== v.toString())
    || Object.entries(oldQuery)
      .some(([k, v]) => k !== 'page' && (newQuery?.[k]?.toString() || '') !== v.toString());
}

function FilterBar(props) {
  const {
    filters = [],
    sorts = [],
    path = '',
    query,
    total,
    entityName,
    variant = 'standard',
    showCountAndSort = true,
  } = props;
  const defaultSort = (sorts.find((item) => item.default === true) || sorts[0])?.value;
  const totalFilters = filters.reduce((accum, { shortName, options }) => (
    accum + Boolean(query?.[shortName]) + Boolean(query?.[options?.childFilters?.shortName])
  ), 0);
  const router = useRouter();
  const currentUser = useCurrentUser();
  const userProfile = useMemo(() => {
    // ignore updated at and recentActivity changes for purposes of updates
    const { updatedAt, recentActivity, ...rest } = currentUser;
    return { ...rest };
  }, [currentUser]);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const handleFilter = () => {
    setFiltersOpen(true);
  };
  const handleSort = () => {
    setSortOpen(true);
  };

  const parseUrlValue = (value, options = {}) => {
    if (options.multiple) {
      return value ? value.split(',') : value;
    }
    return value;
  };

  const [filterState, setFilterState] = useState(
    filters.reduce((accum, { name, shortName, options }) => {
      const newAccum = { ...accum };
      const values = parseUrlValue(query?.[shortName], options) || '';
      const valuesAreValid = !options.array
        || !values || values.every((v) => options.array.some((a) => a.value === v));
      if (valuesAreValid) {
        newAccum[name] = values;
      }
      if (options.childFilters) newAccum[options.childFilters.name] = parseUrlValue(query?.[options.childFilters.shortName], options) || '';
      return newAccum;
    }, query.sort ? { sort: query.sort } : {}),
  );
  const currentSort = filterState.sort || defaultSort;

  // When certain refresh conditions are met, reload the filters
  const [refreshConditions, setRefreshConditions] = useState({
    userProfile: false,
  });
  // refresh condition for userProfile
  useEffect(() => {
    if (userProfile && Object.keys(userProfile).length > 0) {
      setRefreshConditions((prev) => ({ ...prev, userProfile: true }));
    }
  }, [userProfile]);
  // handle any updated refreshConditions
  useEffect(() => {
    const stateUpdate = {};
    Object.entries(refreshConditions).forEach(([condition, met]) => {
      if (met && filters.find((filter) => (
        filter?.refreshConditions?.[condition] && filter.shortName in router.query
      ))) {
        stateUpdate[condition] = false;
      }
    });
    if (Object.keys(stateUpdate).length > 0) {
      setRefreshConditions((prev) => ({ ...prev, ...stateUpdate }));
      router.replace(
        { query: router.query },
        undefined,
        { scroll: true },
      );
    }
  }, [filters, router, refreshConditions]);

  const [possibleFilterChange, setPossibleFilterChange] = useState(false);
  const handleUpdateFilterQuery = useCallback((filterStateSnapshot) => {
    const newQuery = { ...router.query };
    // always reset the page when the user changes filters
    delete newQuery.page;
    // unset the sort if it was changed back to default, overwrite otherwise
    if (filterStateSnapshot.sort === defaultSort || !filterStateSnapshot.sort) {
      delete newQuery.sort;
    } else {
      newQuery.sort = filterStateSnapshot.sort;
    }
    // update or unset other filter values
    filters.flatMap((filter) => (
      filter.options.childFilters ? [filter, filter.options.childFilters] : [filter]
    )).forEach(({ name, shortName }) => {
      const value = filterStateSnapshot[name];
      if (value && value !== false && value !== '' && value?.length !== 0) {
        const strValue = Array.isArray(value) ? value.join(',') : value.toString();
        newQuery[shortName] = strValue;
      } else {
        delete newQuery[shortName];
      }
    });
    if (didQueryChange(newQuery, router.query)) {
      // force server-side rendering when routing to a different page
      if (path) {
        window.location.href = addObjectParamsToPath(path, newQuery);
      } else {
        router.push(
          { query: newQuery },
          undefined,
          { scroll: true },
        );
      }
    }
  }, [defaultSort, filters, path, router]);
  // delay navigation in case user is spamming things
  const handleUpdateFilterQueryDebounced = useDebounced(handleUpdateFilterQuery, 300);

  const eligibilityModule = useQuestionModule('eligibility');
  const handleEligibilityRedirect = useCallback((filterStateSnapshot) => {
    const eligibilityRequiredFilters = filters.filter((filter) => {
      const value = filterStateSnapshot[filter.name];
      return (
        filter?.questionModules?.eligibility
        && value
        && value !== false
        && value !== ''
        && value?.length !== 0
      );
    });
    if (eligibilityRequiredFilters.length === 0) return false;
    const queryParams = new URLSearchParams(window.location.search);
    eligibilityRequiredFilters.forEach((filter) => {
      queryParams.set(filter.shortName, filterStateSnapshot[filter.name]);
    });
    return eligibilityModule?.requireCompletion(`/get-paid/more?${queryParams}`);
  }, [eligibilityModule, filters]);

  useEffect(() => {
    if (!handleEligibilityRedirect(filterState)) {
      if (possibleFilterChange) {
        handleUpdateFilterQueryDebounced(filterState);
        setPossibleFilterChange(false);
      }
    }
  }, [
    filterState,
    handleEligibilityRedirect,
    handleUpdateFilterQueryDebounced,
    possibleFilterChange,
  ]);

  // the user may update the filters during route change
  // this safeguards against a missed possible query change
  useEffect(() => {
    setPossibleFilterChange(true);
  }, [router]);

  const updateFilters = useCallback(() => {
    setFiltersOpen(false);
    setSortOpen(false);
    // update filters next component render
    // to allow filterState to update first
    setPossibleFilterChange(true);
  }, []);

  const handleChange = (event) => {
    setFilterState((state) => ({
      ...state,
      [event.target.name]: event.target.value,
    }));
  };

  const [resetFiltersFlag, setResetFiltersFlag] = useContext(ResetFiltersContext);
  const clearAll = () => {
    setFilterState((state) => Object.fromEntries(
      Object.keys(state).map((name) => [name, '']),
    ));
  };
  useEffect(() => {
    if (resetFiltersFlag) {
      setResetFiltersFlag(false);
      clearAll();
      updateFilters();
    }
  }, [resetFiltersFlag, setResetFiltersFlag, updateFilters]);

  const getFilterProps = (option) => ({
    onChange: handleChange,
    value: filterState[option?.name],
    childValue: filterState[option?.options?.childFilters?.name],
    ...option,
  });

  const mainFilters = filters.filter((item) => Boolean(item.main));
  const mobileMainFilters = filters.filter((item) => Boolean(item.mobileMain));

  return (
    <Box mb={{ xs: 2, md: 4 }}>
      {entityName && (
        <Box width='100%' sx={{ display: { xs: 'block', md: 'none' } }}>
          <Typography variant='h6' fontSize='16px'>{total} {entityName}</Typography>
        </Box>
      )}
      <SelectFieldStyles>
        <Box display={{ xs: 'none', md: 'contents' }}>
          <Grid container flexWrap='nowrap' alignItems='center' spacing={2}>
            <Grid item>
              <OutlinedBadge badgeContent={totalFilters}>
                {variant === 'extended' ? (
                  <FiltersButton
                    variant='contained'
                    size='large'
                    onClick={handleFilter}
                    disableElevation
                  >
                    <FilterList fontSize='medium' />
                  </FiltersButton>
                ) : (
                  <Button
                    variant='contained'
                    startIcon={<FilterList />}
                    onClick={handleFilter}
                    size='large'
                    sx={{ height: '40px', minWidth: '150px' }}
                    disableElevation
                  >
                    All Filters
                  </Button>
                )}
              </OutlinedBadge>
            </Grid>
            {mainFilters.map((config) => (
              <Grid item {...config.gridProps} key={`filter-main-${config.shortName}`}>
                <FiltersSelector
                  {...getFilterProps(config)}
                  onClose={updateFilters}
                />
              </Grid>
            ))}
          </Grid>
          {showCountAndSort && (
            <Grid container spacing={2} my={2} alignItems='flex-end'>
              <Grid item xs>
                <Typography fontWeight={700} fontSize='20px'>{total} {entityName}</Typography>
              </Grid>
              <Grid item>
                <SelectField
                  size='small'
                  name='sort'
                  fullWidth
                  native={false}
                  value={currentSort}
                  onChange={handleChange}
                  onClose={updateFilters}
                  renderValue={(value) => (
                    <Typography variant='body2' color='textSecondary' sx={{ minWidth: '150px' }}>
                      Sort by&nbsp;
                      <Typography component='span' variant='body2' fontWeight={500}>
                        {sorts.find((sort) => sort.value === value).label}
                      </Typography>
                    </Typography>
                  )}
                >
                  {sorts.map((sort) => (
                    <MenuItem value={sort.value} key={sort.value}>
                      {sort.label}
                    </MenuItem>
                  ))}
                </SelectField>
              </Grid>
            </Grid>
          )}
        </Box>
        <Box display={{ xs: 'block', md: 'none' }} my={2}>
          <Grid container flexWrap='nowrap' flexDirection='column' justifyContent='center' rowSpacing={2}>
            {mobileMainFilters.map((config) => (
              <Grid item xs={12} key={`filter-mobileMain-${config.shortName}`}>
                <FiltersSelector
                  {...getFilterProps(config)}
                  onClose={updateFilters}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      </SelectFieldStyles>
      <ResponsiveBottomDrawer>
        <Grid container spacing={1} sx={{ display: { xs: 'flex', md: 'none' } }}>
          <Grid item xs>
            <OutlinedBadge badgeContent={totalFilters} sx={{ width: '100%' }}>
              <Button variant='contained' fullWidth startIcon={<FilterList />} onClick={handleFilter}>Filters</Button>
            </OutlinedBadge>
          </Grid>
          {showCountAndSort && (
            <Grid item xs>
              <Button variant='outlined' fullWidth startIcon={<SortIcon />} onClick={handleSort}>Sort</Button>
            </Grid>
          )}
        </Grid>
      </ResponsiveBottomDrawer>
      <RoundedDrawer
        title='Sort By'
        open={sortOpen}
        onClose={updateFilters}
        footer={<Button variant='contained' fullWidth onClick={updateFilters}>Apply Sort</Button>}
      >
        <RadioGroup
          name='sort'
          value={currentSort}
          onChange={handleChange}
        >
          <Grid container spacing={1}>
            {sorts.map((sort) => (
              <Grid item xs={12} key={sort.value}>
                <FormControlLabel control={<Radio />} value={sort.value} label={sort.label} />
              </Grid>
            ))}
          </Grid>
        </RadioGroup>
      </RoundedDrawer>
      <RoundedDrawer
        title='All Filters'
        open={filtersOpen}
        onClose={updateFilters}
        fullHeight
        footer={(
          <Grid container>
            <Grid item xs>
              <Button onClick={clearAll} sx={{ textDecoration: 'underline' }}>Clear All</Button>
            </Grid>
            <Grid item>
              <Button variant='contained' onClick={updateFilters}>Done</Button>
            </Grid>
          </Grid>
        )}
      >
        <Grid container>
          {filters.map((filter, i) => (
            <Grid item xs={12} mb={3.5} key={filter.name}>
              <FiltersSelector {...getFilterProps(filter)} main={false} />
              {(filter.options.array && i < filters.length - 1) && (
                <Divider sx={{ marginTop: 3.5 }} />
              )}
            </Grid>
          ))}
        </Grid>
      </RoundedDrawer>
    </Box>
  );
}

export default FilterBar;
